/**
 * Weekly-usage day descriptors + a render-time flow-definition transform.
 *
 * The intake usage screens count the seven calendar days ending yesterday. Those
 * day labels depend on the wall-clock day, so they cannot be baked into the
 * bundled flow snapshot at import — each bundled flow source applies them at
 * resolve via `applyWeeklyDayDates`, which rewrites every surface that names a
 * day so they stay consistent with one another (one dating layer: stable element identities and bound day ordinals allow a
 * resumed definition to be safely re-dated to its saved week anchor):
 *   - the weekly picker ("Think back to last week"): relative label + date;
 *   - the typical-week picker: the day's weekday;
 *   - the week-summary tiles: the day's short weekday;
 *   - the `calculate_week_summary` tool config: full weekday accessibility labels.
 * Every other screen and element is returned untouched.
 *
 * English-only by construction: this lives beside the bundled `en` snapshot and
 * the resolve request pins `locale: 'en'`. A future localized flow would author
 * these server-side per ADR-017 rather than extend the arrays here.
 */

import {
  JSON_RENDER_TOOL_CHAIN_HANDLER,
  toolsFromDispatchParams,
} from '@galapagos/catalog-schema/tool-bindings';
import { isUnknownRecord, type FlowDefinition, type FlowScreen } from '@galapagos/shared';
import {
  WEEKLY_USAGE_SCREEN_ID,
  TYPICAL_USAGE_SCREEN_ID,
  WEEK_SUMMARY_SCREEN_ID,
} from './en/screens';

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const WEEKDAY_SHORT_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** State fields for the seven day pickers, most-recent first. */
const DAY_FIELDS = ['day_1', 'day_2', 'day_3', 'day_4', 'day_5', 'day_6', 'day_7'] as const;

/**
 * The static weekday short labels the bundled summary tiles ship with, in
 * `day_1…day_7` order. Used to map a summary tile's placeholder label to its
 * ordinal so it can be re-dated.
 */
const STATIC_SUMMARY_SHORT_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export interface WeeklyDayDescriptor {
  /** Bound state field (`day_1` = yesterday … `day_7` = seven days ago). */
  field: string;
  /** Weekly-picker heading: "Yesterday" for the most recent day, else the weekday. */
  label: string;
  /** Full weekday name (e.g. "Friday") — typical picker + a11y labels. */
  weekday: string;
  /** Three-letter weekday (e.g. "Fri") — summary tiles. */
  weekdayShort: string;
  /** Calendar date shown under the weekly-picker heading, e.g. "May 23". */
  dateLabel: string;
}

/**
 * The seven days ending yesterday, newest-first. `day_1` is yesterday (labelled
 * "Yesterday"); the rest carry their weekday name. Dates are derived with
 * local-date arithmetic so month/year boundaries roll over correctly.
 */
export function buildWeeklyDayDescriptors(today: Date): WeeklyDayDescriptor[] {
  return DAY_FIELDS.map((field, index) => {
    const daysAgo = index + 1;
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysAgo);
    const weekday = WEEKDAY_NAMES[date.getDay()];
    return {
      field,
      label: index === 0 ? 'Yesterday' : weekday,
      weekday,
      weekdayShort: WEEKDAY_SHORT_NAMES[date.getDay()],
      dateLabel: `${MONTH_ABBREVIATIONS[date.getMonth()]} ${date.getDate()}`,
    };
  });
}

/** Extracts the `day_N` / `typical_day_N` ordinal a NumberPicker binds its value to. */
function numberPickerBoundOrdinal(element: unknown): number | undefined {
  if (!isUnknownRecord(element) || element.type !== 'NumberPicker') return undefined;
  if (!isUnknownRecord(element.props) || !isUnknownRecord(element.props.value)) return undefined;
  const path = element.props.value.$bindState;
  if (typeof path === 'string') {
    const match = /\/screen\/(?:typical_)?day_(\d+)$/.exec(path);
    if (match) return Number(match[1]) - 1;
  }
  return undefined;
}

/**
 * Rewrites a `calculate_week_summary` tool's `dayLabels` param, if present,
 * and injects the device-local `anchorDate` — the same `today` that dated
 * the labels — so the persisted week anchor and the labels the member saw can
 * never come from different clocks.
 */
function redateToolCall(item: unknown, weekdays: string[], anchorDate: string): unknown {
  if (!isUnknownRecord(item) || item.tool_name !== 'calculate_week_summary') return item;
  if (!isUnknownRecord(item.parameters) || !Array.isArray(item.parameters.dayLabels)) {
    return item;
  }
  return {
    ...item,
    parameters: { ...item.parameters, dayLabels: weekdays, anchorDate },
  };
}

/**
 * Re-dates the summary screen's `initial_state` fallback view-model: each
 * seeded day accessibility label ("Monday: 0 drinks") gets its static weekday
 * prefix swapped for the dated one, so the pre-calc fallback matches the picker.
 */
function redateSummaryInitialState(
  initialState: FlowScreen['initial_state'],
  descriptors: WeeklyDayDescriptor[],
): FlowScreen['initial_state'] {
  if (!initialState) return initialState;
  const screenState = initialState.screen;
  const summary = screenState.baseline_week_summary;
  if (!isUnknownRecord(summary) || !Array.isArray(summary.days)) return initialState;

  const rawDays: unknown[] = summary.days;
  const days = rawDays.map((day, index) => {
    const descriptor = descriptors[index];
    const colon =
      isUnknownRecord(day) && typeof day.accessibilityLabel === 'string'
        ? day.accessibilityLabel.indexOf(':')
        : -1;
    if (!descriptor || !isUnknownRecord(day) || typeof day.accessibilityLabel !== 'string') {
      return day;
    }
    if (colon < 0) return day;
    return {
      ...day,
      accessibilityLabel: `${descriptor.weekday}${day.accessibilityLabel.slice(colon)}`,
    };
  });

  return {
    ...initialState,
    screen: { ...screenState, baseline_week_summary: { ...summary, days } },
  };
}

/** Rewrites the day labels on one screen's elements for `descriptors`. */
function redateScreen(
  screen: FlowScreen,
  descriptors: WeeklyDayDescriptor[],
  anchorDate: string,
): FlowScreen {
  const weekdays = descriptors.map((d) => d.weekday);
  const isWeekly = screen.screen_id === WEEKLY_USAGE_SCREEN_ID;
  const isTypical = screen.screen_id === TYPICAL_USAGE_SCREEN_ID;
  const isSummary = screen.screen_id === WEEK_SUMMARY_SCREEN_ID;

  const elements: Record<string, unknown> = {};
  for (const [elementId, element] of Object.entries(screen.spec.elements)) {
    if (!isUnknownRecord(element)) {
      elements[elementId] = element;
      continue;
    }
    const props = isUnknownRecord(element.props) ? element.props : null;
    let next: Record<string, unknown> = element;

    // Day-picker rows (weekly + typical).
    const ordinal = numberPickerBoundOrdinal(element);
    if ((isWeekly || isTypical) && ordinal !== undefined && descriptors[ordinal]) {
      const d = descriptors[ordinal];
      if (props) {
        next = {
          ...next,
          props: isWeekly
            ? {
                ...props,
                label: d.label,
                subtitle: d.dateLabel,
                decrementAccessibilityLabel: `Decrease count for ${d.label}, ${d.dateLabel}`,
                incrementAccessibilityLabel: `Increase count for ${d.label}, ${d.dateLabel}`,
              }
            : {
                ...props,
                label: d.weekday,
                decrementAccessibilityLabel: `Decrease count for ${d.weekday}`,
                incrementAccessibilityLabel: `Increase count for ${d.weekday}`,
              },
        };
      }
    }

    // Summary tiles: a static short-weekday Text placeholder → the real short weekday.
    if (isSummary && element.type === 'Text' && typeof props?.value === 'string') {
      const staticIndex = STATIC_SUMMARY_SHORT_LABELS.findIndex(
        (label) => elementId === `day${label}Label`,
      );
      if (staticIndex !== -1 && descriptors[staticIndex]) {
        next = { ...next, props: { ...props, value: descriptors[staticIndex].weekdayShort } };
      }
    }

    // Continue-button tool config carrying the summary a11y day labels.
    const on = isUnknownRecord(next.on) ? next.on : null;
    if (Array.isArray(on?.press)) {
      const press = on.press;
      const redated = press.map((item) => redateToolCall(item, weekdays, anchorDate));
      next = { ...next, on: { ...on, press: redated } };
    } else if (
      isUnknownRecord(on?.press) &&
      on.press.action === JSON_RENDER_TOOL_CHAIN_HANDLER &&
      isUnknownRecord(on.press.params)
    ) {
      const binding = on.press;
      const params = on.press.params;
      next = {
        ...next,
        on: {
          ...on,
          press: {
            ...binding,
            params: {
              ...params,
              tools: toolsFromDispatchParams(params).map((item) =>
                redateToolCall(item, weekdays, anchorDate),
              ),
            },
          },
        },
      };
    }

    elements[elementId] = next;
  }

  return {
    ...screen,
    spec: { ...screen.spec, elements },
    ...(isSummary
      ? { initial_state: redateSummaryInitialState(screen.initial_state, descriptors) }
      : {}),
  };
}

/**
 * Returns a copy of `definition` with the usage screens' day labels dated for
 * `today`. Non-usage screens and every other element are returned untouched.
 */
export function applyWeeklyDayDates(definition: FlowDefinition, today: Date): FlowDefinition {
  const descriptors = buildWeeklyDayDescriptors(today);
  // Device-local calendar date of the same 'today' that dated the labels —
  // injected into the summary tool config as the persisted week anchor.
  const anchorDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const redatedIds = new Set([
    WEEKLY_USAGE_SCREEN_ID,
    TYPICAL_USAGE_SCREEN_ID,
    WEEK_SUMMARY_SCREEN_ID,
  ]);

  const screens: Record<string, FlowScreen> = {};
  for (const [stepKey, screen] of Object.entries(definition.screens)) {
    screens[stepKey] = redatedIds.has(screen.screen_id)
      ? redateScreen(screen, descriptors, anchorDate)
      : screen;
  }

  return { ...definition, screens };
}
